import { createAction } from '@reduxjs/toolkit'
import { TrainingVideo } from 'admin/interfaces'
import { impersonationSelector } from 'admin/services/selectors'
import { firstValueFrom } from 'rxjs'
import { formatProducts, setProducts } from 'services/product'
import { getAccountInfo } from 'services/settings/actions'
import { PPProduct, User, sortByKeyAscending } from 'shared'
import StoreState, { StoreThunkAction, StoreThunkDispatch, createAppAsyncThunk, getState } from 'store/state'
import toCamelCase from 'utils/toCamelCase'
import * as api from './net'
import { currentUserSelector, trainingVideosSelector, trialTasksSelector, userSubscriptionsSelector } from './selectors'

const BASE_TEN = 10

export const getUserSuccess = createAction(
  'ACTION_GET_USER_SUCCESS',
  (payload: any, product: PPProduct) => {
    const user: User = toCamelCase(payload) as any
    const v3 = (user as any).v3

    return {
      payload: {
        ...user,
        lastLoginTime: Date.now(),
        v3: v3 !== null && v3 > 0,
        v3Status: parseInt(v3, BASE_TEN),
        productInfo: product,
        finishedSetup: parseInt(user.finishedSetup as any, BASE_TEN)
      }
    }
  }

)

export function checkSubscription(): StoreThunkAction<Promise<any>> {
  return (dispatch: StoreThunkDispatch, getState: getState) => {
    const isImpersonating = impersonationSelector(getState()) !== undefined
    return dispatch(getAccountInfo())
      .unwrap()
      .then((data: any) => {
        const productInfo = data.productInfo
        if (isImpersonating) {
          return
        }

        dispatch(checkSubscriptionSuccess(productInfo.id, productInfo, undefined, { account: data.raw.account }))
      })
      .catch((error: any) => {
        console.log('[checkSubscription()] Error: ', error)
      })
  }
}

export const checkSubscriptionSuccess = createAction(
  'ACTION_CHECK_SUBSCRIPTION_SUCCESS',
  (
    product: string,
    productInfo: PPProduct,
    roles?: {
      isAdmin: boolean,
      isBeta: boolean,
      hasThreads: boolean,
      igDirectEnabled?: boolean,
      hasMultipleComments?: boolean,
      manualInsightsRefresh?: boolean
    },
    user?: any
  ) => {
    return {
      payload: {
        product,
        productInfo,
        roles,
        user
      }
    }
  }
)

export const updateUserProfile = createAppAsyncThunk(
  'user/updateProfile',
  async (args: { firstName?: string, lastName?: string, introProgress?: number, email?: string }, { dispatch, rejectWithValue }) => {
    const { firstName, lastName, introProgress, email } = args
    const response = await firstValueFrom(dispatch(api.updateProfile(firstName, lastName, introProgress, email)))
    if (response.error) {
      return rejectWithValue(response.error.message)
    }
    return response
  }
)

export const updateIntroProgress = createAppAsyncThunk(
  'user/updateIntroProgress',
  async (step: number, { dispatch }) => {
    await firstValueFrom(dispatch(api.updateProfile(undefined, undefined, step)))
    return step
  }
)

export const getTrialTasks = createAppAsyncThunk(
  'training/getTasks',
  async (force: boolean = false, { dispatch, getState }) => {
    if (!force) {
      const cached = trialTasksSelector(getState() as StoreState)
      if (cached && cached.length > 0) {
        return cached
      }
    }

    const data = await firstValueFrom(dispatch(api.getTrialEvents()))

    const formatted = data.map((task: any) => ({
      ...task,
      order: parseInt(task.order, 10),
      value: task.totalQuantity,
      maxValue: task.maxQuantity,
      discount: task.percent,
      completed: Boolean(task.completed)
    })).sort(sortByKeyAscending('order'))

    return formatted
  }
)

export const getTrialTaskGroups = createAppAsyncThunk(
  'trial/getTaskGroups',
  async (_, { dispatch }) => {
    const response = await firstValueFrom(dispatch(api.getTrialEventGroups()))
    return response.map((data: any) => data.map((group: any) => ({
      ...group,
      order: parseInt(group.order, 10),
      tasks: []
    })))
  }
)

export const fetchCouponCode = createAppAsyncThunk(
  'trial/fetchCouponCode',
  async () => {
    const response = await api.getCouponCode()
    return response
  }
)

export const fetchTrainingVideos = createAppAsyncThunk(
  'training/fetchTrainingVideos',
  async (sectionId: string = '', { dispatch }) => {
    const res = await firstValueFrom(dispatch(api.getTrainingVideos(sectionId)))
    return res
  }
)

export const fetchTrainingVideoSections = createAppAsyncThunk(
  'training/fetchTrainingVideoSections',
  async (force: boolean = false, { dispatch, getState }) => {
    if (!force) {
      const cached = trainingVideosSelector(getState() as StoreState)
      if (cached && cached.length > 0) {
        return cached
      }
    }
    const [sections, videos] = await Promise.all([
      firstValueFrom(dispatch(api.getTrainingVideoSections())),
      firstValueFrom(dispatch(api.getTrainingVideos()))
    ])

    const vidsBySection = videos.reduce((acc: Record<string, TrainingVideo[]>, video: any) => {
      if (!acc[video.sectionId]) {
        acc[video.sectionId] = []
      }
      acc[video.sectionId].push({
        id: video.id,
        url: video.url || '',
        title: video.title,
        order: parseInt(video.order, 10),
        sectionId: video.sectionId,
        thumbnailUrl: video.thumbnailUrl,
        actionUrl: video.actionUrl,
        taskCode: video.taskCode
      })
      return acc
    }, {} as Record<string, TrainingVideo[]>)

    const formatted = sections.map((section: any) => ({
      id: section.id,
      title: section.title,
      order: parseInt(section.order, 10),
      slug: section.slug || section.title.toLowerCase().split(' ').join('-'),
      label: section.label || section.title,
      isVisible: section.isVisible === '1',
      videos: (vidsBySection[section.id] || []).sort(sortByKeyAscending('order'))
    })).sort(sortByKeyAscending('order'))

    return formatted
  }
)

export { trackTrainingEvent } from './tracking'

export const getUserSubscription = createAppAsyncThunk(
  'user/getSubscription',
  async (force: boolean = false, { dispatch, rejectWithValue, getState }) => {
    const cached = userSubscriptionsSelector(getState() as StoreState)
    const user = currentUserSelector(getState() as StoreState)
    if (!force && cached) {
      return cached
    }
    if (!user?.account?.stripeSubscriptionId) {
      return rejectWithValue('No subscription found')
    }
    try {
      const response = await firstValueFrom(dispatch(api.getSubscription()))
      return response.subscription
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getCustomerPortalSession = createAppAsyncThunk(
  'user/getCustomerPortalSession',
  async (returnUrl: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await firstValueFrom(dispatch(api.getCustomerPortalSession(returnUrl)))
      return response.url
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const updateUserSubscriptionTier = createAppAsyncThunk(
  'user/updateSubscriptionTier',
  async ({ tier, currency }: { tier: number, currency?: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await firstValueFrom(dispatch(api.setUserSubscriptionTier(tier, currency)))
      if (response.subscriptions?.products) {
        const products = formatProducts(response.subscriptions.products as PPProduct[])
        dispatch(setProducts(products))
      }
      return response
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
